import type { DropboxFile } from '@/components/common/DropboxChooser'
import type { GoogleDriveFile } from '@/components/common/GoogleDrivePicker'
import type { OneDriveFile } from '@/components/common/OneDrivePicker'

/** The provider keys the API's cloud imports take (`Provider` / `CloudStorageProvider`). */
export type CloudProvider = 'GoogleDrive' | 'OneDrive' | 'Dropbox'

export const CLOUD_PROVIDERS: readonly CloudProvider[] = ['GoogleDrive', 'OneDrive', 'Dropbox']

export const CLOUD_PROVIDER_LABEL: Readonly<Record<CloudProvider, string>> = {
    GoogleDrive: 'Google Drive',
    OneDrive: 'OneDrive',
    Dropbox: 'Dropbox',
}

interface PickedCloudFileBase {
    fileId: string
    fileName: string
    mimeType: string
    /** Undefined when the provider did not say; never read that as 0. */
    sizeBytes?: number
}

/** A file picked in any of the three providers, in one shape. Each caller maps it onto its own request. */
export type PickedCloudFile =
    | (PickedCloudFileBase & { provider: 'GoogleDrive' | 'OneDrive' })
    /** The Chooser's direct content link is what the API downloads a Dropbox file from. */
    | (PickedCloudFileBase & { provider: 'Dropbox'; directDownloadUrl: string })

export function fromGoogleDrive(file: GoogleDriveFile): PickedCloudFile {
    return { provider: 'GoogleDrive', fileId: file.fileId, fileName: file.fileName, mimeType: file.mimeType, sizeBytes: file.sizeBytes }
}

export function fromOneDrive(file: OneDriveFile): PickedCloudFile {
    return { provider: 'OneDrive', fileId: file.fileId, fileName: file.fileName, mimeType: file.mimeType, sizeBytes: file.sizeBytes }
}

/** The Chooser gives one link, as both its direct content URL and its download link; it is the direct one here. */
export function fromDropbox(file: DropboxFile): PickedCloudFile {
    return {
        provider: 'Dropbox', fileId: file.fileId, fileName: file.fileName, mimeType: file.mimeType,
        sizeBytes: file.bytes, directDownloadUrl: file.directDownloadUrl,
    }
}
